import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { useSession } from '@/lib/session-context'
import { DEFAULT_ACTIVATION_STATE } from '@/lib/activation-default-state'
import {
  generateActivationAreaSuggestions,
  generateStepThreeMarketAnalyses,
  getActivationMarketScanRun,
  getActivationProfile,
  saveActivationStep,
  scanActivationMarkets,
  sendStepOneAgentMessage,
  sendStepTwoAgentMessage,
  sendStepFiveAgentMessage,
} from '@/lib/api'

const STORAGE_PREFIX = 'sentinel.activation.v1:'

const ActivationContext = createContext(null)

function storageKey(userId) {
  return userId ? `${STORAGE_PREFIX}${userId}` : null
}

function loadState(userId) {
  const key = storageKey(userId)
  if (!key) return DEFAULT_ACTIVATION_STATE
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return DEFAULT_ACTIVATION_STATE
    const parsed = JSON.parse(raw)
    return mergeActivationState(DEFAULT_ACTIVATION_STATE, parsed)
  } catch {
    return DEFAULT_ACTIVATION_STATE
  }
}

function mergeActivationState(localState, remoteState) {
  if (!remoteState) return localState
  if (remoteState.persisted === false) return localState

  const state = { ...remoteState }
  delete state.persisted

  const mergedStep2 = {
    ...localState.step2,
    ...state.step2,
    strategyAttributes: {
      ...localState.step2?.strategyAttributes,
      ...state.step2?.strategyAttributes,
    },
  }
  const mergedStep3 = mergeStepThreeState(localState.step3, state.step3)

  return {
    ...localState,
    ...state,
    step1: { ...localState.step1, ...state.step1 },
    step2: mergedStep2,
    step3: mergedStep3,
    step4: { ...localState.step4, ...state.step4 },
    step5: { ...localState.step5, ...state.step5 },
  }
}

function mergeStepThreeState(localStep3 = {}, remoteStep3 = {}) {
  const mergedStep3 = { ...localStep3, ...remoteStep3 }

  if (!remoteStep3?.areaSuggestions && localStep3?.areaSuggestions) {
    mergedStep3.areaSuggestions = localStep3.areaSuggestions
  }
  if (!remoteStep3?.areaRecommendations && localStep3?.areaRecommendations) {
    mergedStep3.areaRecommendations = localStep3.areaRecommendations
  }
  if (!remoteStep3?.areaMarketAnalysis && localStep3?.areaMarketAnalysis) {
    mergedStep3.areaMarketAnalysis = localStep3.areaMarketAnalysis
  }

  preserveLocalDraftValue(mergedStep3, localStep3, remoteStep3, 'preferredLocation')
  preserveLocalDraftValue(mergedStep3, localStep3, remoteStep3, 'maxTravelValue')
  preserveLocalDraftValue(mergedStep3, localStep3, remoteStep3, 'travelUnit')
  preserveLocalDraftValue(mergedStep3, localStep3, remoteStep3, 'selectedMarketId')
  preserveLocalDraftValue(mergedStep3, localStep3, remoteStep3, 'postcodeRecommendationsMarketId')
  preserveLocalDraftValue(mergedStep3, localStep3, remoteStep3, 'selectedPostcodes')
  preserveLocalDraftValue(mergedStep3, localStep3, remoteStep3, 'selectedPostcodeTargets')

  return mergedStep3
}

function preserveLocalDraftValue(mergedStep3, localStep3, remoteStep3, key) {
  if (hasPersistedValue(remoteStep3?.[key]) || !hasPersistedValue(localStep3?.[key])) return

  mergedStep3[key] = localStep3[key]
}

function hasPersistedValue(value) {
  if (Array.isArray(value)) return value.length > 0

  return value !== null && value !== undefined && value !== ''
}

function invalidatedStepThreeState(currentStep3) {
  return {
    ...currentStep3,
    selectedPostcodes: [],
    selectedMarketId: null,
    postcodeRecommendationsMarketId: null,
    areaSuggestions: null,
    areaMarketAnalysis: null,
    areaRecommendations: null,
  }
}

export function ActivationProvider({ children }) {
  const { currentUser, csrfToken } = useSession()
  const userId = currentUser?.id ?? null
  const [state, setState] = useState(() => loadState(userId))
  const [hydratedUserId, setHydratedUserId] = useState(null)
  const [serverError, setServerError] = useState(null)
  const lastUserIdRef = useRef(userId)
  const skipSyncRef = useRef(false)

  useEffect(() => {
    if (lastUserIdRef.current !== userId) {
      if (lastUserIdRef.current) {
        window.localStorage.removeItem(`${STORAGE_PREFIX}${lastUserIdRef.current}`)
      }
      lastUserIdRef.current = userId
      skipSyncRef.current = true
      setState(loadState(userId))
    }
  }, [userId])

  useEffect(() => {
    if (!userId) return undefined

    let active = true

    getActivationProfile({ csrfToken })
      .then((payload) => {
        if (!active) return
        skipSyncRef.current = true
        setState((prev) => mergeActivationState(prev, payload.activation))
        setServerError(null)
      })
      .catch((error) => {
        if (!active) return
        setServerError(error.message)
      })
      .finally(() => {
        if (active) setHydratedUserId(userId)
      })

    return () => { active = false }
  }, [csrfToken, userId])

  useEffect(() => {
    if (skipSyncRef.current) {
      skipSyncRef.current = false
      return
    }
    const key = storageKey(userId)
    if (!key) return
    window.localStorage.setItem(key, JSON.stringify(state))
  }, [state, userId])

  const setStepData = (n, partial) => {
    setState((prev) => ({ ...prev, [`step${n}`]: { ...prev[`step${n}`], ...partial } }))
  }

  const advanceTo = (n) => {
    setState((prev) => (n > prev.currentStep ? { ...prev, currentStep: n } : prev))
  }

  const reset = () => {
    const key = storageKey(userId)
    if (key) window.localStorage.removeItem(key)
    skipSyncRef.current = true
    setState(DEFAULT_ACTIVATION_STATE)
  }

  const saveStep = async (n, values, options = {}) => {
    setServerError(null)
    const payload = await saveActivationStep(n, values, { csrfToken })
    skipSyncRef.current = true
    setState((prev) => {
      const merged = mergeActivationState(prev, payload.activation)
      const upstreamSaveInvalidatedRecommendations = (n === 1 || n === 2)
        && payload.activation?.step3?.areaSuggestions === null
      const next = upstreamSaveInvalidatedRecommendations
        ? { ...merged, step3: invalidatedStepThreeState(merged.step3) }
        : merged
      const preservedStepData = options.preserveStepData
      if (!preservedStepData) return next

      const stepKey = `step${n}`
      return {
        ...next,
        [stepKey]: {
          ...next[stepKey],
          ...Object.fromEntries(
            Object.entries(preservedStepData).filter(([key, value]) => value && !next[stepKey]?.[key]),
          ),
        },
      }
    })
    return payload.activation
  }

  const scanMarkets = async ({ force = false } = {}) => {
    setServerError(null)
    const payload = await scanActivationMarkets({ csrfToken, force })
    if (!payload.activation) return payload

    skipSyncRef.current = true
    setState((prev) => mergeActivationState(prev, payload.activation))
    return payload.run ? payload : payload.activation
  }

  const getMarketScanRun = async (runId) => {
    setServerError(null)
    const payload = await getActivationMarketScanRun(runId, { csrfToken })
    if (payload.activation) {
      skipSyncRef.current = true
      setState((prev) => mergeActivationState(prev, payload.activation))
    }
    return payload
  }

  const generateAreaSuggestions = async (values) => {
    setServerError(null)
    const payload = await generateActivationAreaSuggestions(values, { csrfToken })
    setState((prev) => ({
      ...prev,
      step3: {
        ...prev.step3,
        preferredLocation: values.preferredLocation,
        maxTravelValue: values.maxTravelValue,
        travelUnit: values.travelUnit || prev.step3.travelUnit,
        selectedPostcodes: [],
        selectedMarketId: null,
        postcodeRecommendationsMarketId: null,
        areaSuggestions: payload,
        areaMarketAnalysis: null,
        areaRecommendations: null,
      },
    }))
    return payload
  }

  const generateStepThreeMarketAnalysis = async (values) => {
    setServerError(null)
    const payload = await generateStepThreeMarketAnalyses(values, { csrfToken })
    const areaMarketAnalysis = {
      assistantMessage: payload.assistantMessage || '',
      marketAnalyses: payload.marketAnalyses || [],
      deprioritizedAreas: payload.deprioritizedAreas || [],
    }

    setState((prev) => ({
      ...prev,
      step3: {
        ...prev.step3,
        preferredLocation: values.preferredLocation,
        maxTravelValue: values.maxTravelValue,
        travelUnit: values.travelUnit || prev.step3.travelUnit,
        selectedPostcodes: [],
        selectedMarketId: null,
        postcodeRecommendationsMarketId: null,
        areaSuggestions: payload.areaSuggestions || null,
        areaMarketAnalysis,
        areaRecommendations: null,
      },
    }))
    return payload
  }

  const askStepOneAgent = async (values) => {
    setServerError(null)
    return sendStepOneAgentMessage(values, { csrfToken })
  }

  const askStepTwoAgent = async (values) => {
    setServerError(null)
    return sendStepTwoAgentMessage(values, { csrfToken })
  }

  const askStepFiveAgent = async (values) => {
    setServerError(null)
    return sendStepFiveAgentMessage(values, { csrfToken })
  }

  return (
    <ActivationContext
      value={{
        ...state,
        isHydrating: Boolean(userId && hydratedUserId !== userId),
        serverError,
        setStepData,
        advanceTo,
        reset,
        saveStep,
        askStepOneAgent,
        askStepTwoAgent,
        askStepFiveAgent,
        generateAreaSuggestions,
        generateStepThreeMarketAnalysis,
        scanMarkets,
        getMarketScanRun,
      }}
    >
      {children}
    </ActivationContext>
  )
}

export function useActivation() {
  const ctx = useContext(ActivationContext)
  if (!ctx) throw new Error('useActivation must be used inside ActivationProvider')
  return ctx
}
